import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import "./payment.css";
import razorpayLogo from "../../../../assets/icons/razorpayLogo.svg";
import { useDispatch } from "react-redux";
import { useWindowDimensions } from "../../../../utils/util";
import { reSchedule, makePayment, verifyPayment, bookTrial } from "../../../../store/actions/student";
import { toast } from "react-toastify";
import Navigation from "../../../../landing/components/Nav";

// function to load Razorpay script
function loadScript(src) {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

function Payment() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { width } = useWindowDimensions();

  const state = location.state || {};

  const trail = state.trail || false;
  const teacherData = state.teacherData || null;
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const couponDetails = state.couponDetails || null;

  const couponDiscountPercentage = couponDetails?.discountAmt || 0;

  const chosenEvent = useMemo(() => state.event || JSON.parse(localStorage.getItem("chosenEvent")) || {}, [state.event]);
  const course = useMemo(() => state.course || JSON.parse(localStorage.getItem("chosenCourse")) || {}, [state.course]);
  const studentData = JSON.parse(localStorage.getItem("studentData")) || {};
  const rescheduleObj = state.rescheduleObj || JSON.parse(localStorage.getItem("rescheduleObj")) || null;
  const bookingAvailabilityIds = useMemo(() => chosenEvent?.availabilityIds || (chosenEvent?.id || chosenEvent?._id ? [chosenEvent?.id || chosenEvent?._id] : []), [chosenEvent]);

  useEffect(() => {
    const hasTeacher = teacherData || course?.userId?.onType;
    const hasCourse = course?._id || course?.id;
    const hasEvent = chosenEvent?.start || chosenEvent?.from;

    if (!hasTeacher || !hasEvent || !hasCourse) {
      toast.error("Session data missing. Redirecting...");
      navigate("/find-teacher");
    }
  }, [teacherData, chosenEvent, course, navigate]);

  const [slotStart] = useState(new Date(chosenEvent.start || Date.now()));
  const [slotEnd] = useState(new Date(chosenEvent.end || Date.now()));

  const [selectedPlan, setSelectedPlan] = useState({});
  const platformFeesPercentage = 6;
  const platformFeesMultiplier = platformFeesPercentage / 100;

  // Sync summary with backend
  useEffect(() => {
    async function getSummary() {
      if (trail || rescheduleObj || !course?._id || !selectedPlan?.number) {
        setPaymentDetails(null);
        return;
      }

      const packageType = selectedPlan.number ? selectedPlan.number.split(" ")[0] : "1";

      const tId = teacherData?._id || teacherData?.id || course?.userId?.onType?._id || course?.userId?.onType?.id || (typeof course?.userId?.onType === 'string' ? course.userId.onType : undefined);
      const cId = course?._id || course?.id;

      const payload = {
        courseId: cId,
        teacherId: tId,
        availabilityIds: bookingAvailabilityIds,
        packageType: packageType === "Course" ? "1" : packageType,
        from: chosenEvent?.start || chosenEvent?.from,
        to: chosenEvent?.end || chosenEvent?.to
      };


      const result = await dispatch(makePayment(payload));

      if (result?.success) {
        setPaymentDetails(result.paymentDetails);
      }
    }
    getSummary();
  }, [dispatch, course, chosenEvent, selectedPlan, trail, rescheduleObj, teacherData, bookingAvailabilityIds]);

  const basePrice = paymentDetails?.itemPrice || selectedPlan?.itemPrice || 0;
  const platformFees = paymentDetails?.platformFees || (trail || rescheduleObj ? 0 : Math.round(basePrice * platformFeesMultiplier));
  const totalAmount = paymentDetails?.total || (trail || rescheduleObj ? 0 : basePrice + platformFees);
  const couponDiscountPrice = paymentDetails?.couponDiscount || 0;
  const displayTotal = paymentDetails?.displayInfo?.total;

  const lessonPrices = React.useMemo(() => {
    const symbol = course.currency === "USD" ? "$" : course.currency === "INR" ? "₹" : course.currency || "₹";

    if (trail) {
      return [{ number: "30 Minute Trial Lesson", itemPrice: 0, actual: `${symbol} 0/hrs` }];
    } else if (rescheduleObj) {
      return [{ number: "1 Lesson", itemPrice: 0, actual: `${symbol} 0/hrs` }];
    } else if (course.price1?.data && course.price2?.data) {
      return [
        { ...course, number: "1 Lesson", itemPrice: course.price?.data || 0, actual: `${symbol}${course.price?.data || 0}/hr` },
        { ...course, number: "5 Lessons", itemPrice: course.price1?.data || 0, actual: `${symbol}${course.price1?.data || 0}/hr` },
        { ...course, number: "10 Lessons", itemPrice: course.price2?.data || 0, actual: `${symbol}${course.price2?.data || 0}/hr` },
      ];
    } else if (course.price1?.data) {
      return [
        { ...course, number: "1 Lesson", itemPrice: course.price?.data || 0, actual: `${symbol}${course.price?.data || 0}/hr` },
        { ...course, number: "5 Lessons", itemPrice: course.price1?.data || 0, actual: `${symbol}${course.price1?.data || 0}/hr` },
      ];
    } else {
      return [{ ...course, number: "Course Price", itemPrice: course.price?.data || 0, actual: `${symbol}${course.price?.data || 0}/hr` }];
    }
  }, [trail, rescheduleObj, course]);

  useEffect(() => {
    if (!selectedPlan?.number && lessonPrices.length > 0) {
      setSelectedPlan(lessonPrices[0]);
    }
  }, [lessonPrices, selectedPlan?.number]);






  function convertHours(h, m) {
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    m = m.toString().padStart(2, "0");
    return `${h}:${m} ${ampm}`;
  }


  const pay = async () => {
    if (loading) return;
    setLoading(true);

    if (trail) {
      try {
        const payload = {
          teacherId: teacherData?._id || teacherData?.id,
          courseId: course?._id || course?.id,
          availabilityIds: bookingAvailabilityIds,
          from: chosenEvent?.start,
          to: chosenEvent?.end
        };

        if (!payload.availabilityIds || payload.availabilityIds.length === 0) {
          toast.error("Time slot not found. Please try again.");
          setLoading(false);
          return;
        }

        const res = await dispatch(bookTrial(payload));
        if (res.success) {
          toast.success("Free trial booked successfully!");

          // Update local profile/studentData to hide the book trial button immediately
          const profile = JSON.parse(localStorage.getItem("profile")) || {};
          const studentData = JSON.parse(localStorage.getItem("studentData")) || {};
          profile.hasTakenFreeTrial = true;
          studentData.hasTakenFreeTrial = true;
          localStorage.setItem("profile", JSON.stringify(profile));
          localStorage.setItem("studentData", JSON.stringify(studentData));

          const role = JSON.parse(localStorage.getItem("profile"))?.role.toLowerCase() || "student";
          navigate(`/${role}/dashboard`);
        } else {
          toast.error(res.message || "Failed to book trial");
          setLoading(false);
        }
      } catch (err) {
        console.error("Trial booking error:", err);
        toast.error("An error occurred during booking");
        setLoading(false);
      }
      return;
    }

    if (rescheduleObj) {
      // Reschedule Flow
      try {
        const payload = {
          session: rescheduleObj,
          availabilityIds: bookingAvailabilityIds,
          from: chosenEvent?.start,
          to: chosenEvent?.end
        };
        const res = await dispatch(reSchedule(payload));
        if (res.success) {
          toast.success("Session rescheduled successfully!");
          const role = JSON.parse(localStorage.getItem("profile"))?.role.toLowerCase() || "student";
          navigate(`/${role}/dashboard`);
        } else {
          toast.error(res.message || "Failed to reschedule");
          setLoading(false);
        }
      } catch (err) {
        console.error("Reschedule error:", err);
        toast.error("An error occurred during rescheduling");
        setLoading(false);
      }
      return;
    }

    // Standard Razorpay Order Flow (Popup)
    const sdkLoaded = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
    if (!sdkLoaded) {
      toast.error("Razorpay SDK failed to load.");
      setLoading(false);
      return;
    }

    try {
      const packageType = selectedPlan?.number ? selectedPlan.number.split(" ")[0] : "1";

      const tId = teacherData?._id || teacherData?.id || course?.userId?.onType?._id || course?.userId?.onType?.id || (typeof course?.userId?.onType === 'string' ? course.userId.onType : undefined);
      const cId = course?._id || course?.id;

      const bookingData = {
        courseId: cId,
        teacherId: tId,
        availabilityIds: bookingAvailabilityIds,
        packageType: packageType === "Course" ? "1" : packageType,
        from: chosenEvent?.start || chosenEvent?.from,
        to: chosenEvent?.end || chosenEvent?.to,
        couponCode: couponDetails?.couponName
      };



      if (!bookingData.courseId || !bookingData.teacherId || !bookingData.availabilityIds || bookingData.availabilityIds.length === 0) {
        console.error("Missing booking data:", bookingData);
        toast.error("Required booking information is missing. Please select a time slot again.");
        setLoading(false);
        return;
      }

      const initiateRes = await dispatch(makePayment(bookingData));


      if (!initiateRes?.success) {
        toast.error(initiateRes?.message || initiateRes?.error || "Payment initiation failed");
        setLoading(false);
        return;
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_live_SE3c1MDeNzJunk",
        amount: initiateRes.amount,
        currency: initiateRes.currency || "INR",
        name: "Neurolingua",
        description: `Booking for ${course?.title?.data || course?.title}`,
        image: razorpayLogo,
        order_id: initiateRes.id,
        handler: async (response) => {
          const verifyData = {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            bookingData
          };

          const verifyResult = await dispatch(verifyPayment(verifyData));
          if (verifyResult.success) {
            toast.success("Payment successful and session booked!");
            const profile = JSON.parse(localStorage.getItem("profile"));
            const role = profile?.role?.toLowerCase() || "student";
            navigate(`/${role}/dashboard`);
          } else {
            toast.error(verifyResult.message || "Booking verification failed");
            setLoading(false);
          }
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
          }
        },
        prefill: {
          name: studentData?.fullName || studentData?.firstName || "",
          email: studentData?.email || "",
        },
        theme: { color: "#fe1848" },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();

    } catch (err) {
      console.error("Payment error:", err);
      toast.error("An error occurred during payment");
      setLoading(false);
    }
  };

  return (
    <div className='parent-container' style={{ background: '#f8f9fa' }}>
      <Navigation />

      <div className="container" style={{ maxWidth: '1100px', margin: '0 auto', padding: '120px 20px 60px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: width > 992 ? '1.5fr 1fr' : '1fr', gap: '40px' }}>

          {/* Left Column: Details & Plan Selection */}
          <div>
            <div style={{ background: 'white', borderRadius: '20px', padding: '30px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', marginBottom: '30px' }}>
              <h2 style={{ fontSize: '1.8rem', fontWeight: '800', marginBottom: '25px', color: '#333' }}>Checkout Summary</h2>

              <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '30px', padding: '20px', background: '#fff5f6', borderRadius: '15px' }}>
                <img
                  src={teacherData?.teacherProfilePic?.data || teacherData?.teacherProfilePic}
                  alt="Teacher"
                  style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '3px solid white', boxShadow: '0 5px 15px rgba(0,0,0,0.1)' }}
                />
                <div>
                  <h4 style={{ margin: 0, fontWeight: '700', fontSize: '1.2rem' }}>
                    {teacherData?.firstName?.data || teacherData?.firstName} {teacherData?.lastName?.data || teacherData?.lastName}
                  </h4>
                  <p style={{ margin: '5px 0 0', color: '#fe1848', fontWeight: '600' }}>
                    {course?.title?.data || course?.title || "Expert Instruction"}
                  </p>
                </div>
              </div>

              <div style={{ marginBottom: '30px' }}>
                <h5 style={{ fontWeight: '700', marginBottom: '15px' }}>Schedule</h5>
                <div style={{ padding: '15px', background: '#f9f9f9', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <i className="far fa-calendar-alt" style={{ color: '#fe1848' }}></i>
                    <span style={{ fontWeight: '500' }}>{slotStart.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <i className="far fa-clock" style={{ color: '#fe1848' }}></i>
                    <span style={{ fontWeight: '500' }}>{convertHours(slotStart.getHours(), slotStart.getMinutes())} - {convertHours(slotEnd.getHours(), slotEnd.getMinutes())}</span>
                  </div>
                </div>
              </div>

              {!trail && !rescheduleObj && (
                <div>
                  <h5 style={{ fontWeight: '700', marginBottom: '15px' }}>Select Lesson Package</h5>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {lessonPrices.map((plan, i) => (
                      <div
                        key={i}
                        onClick={() => {
                          setSelectedPlan(plan);
                        }}
                        style={{
                          padding: '20px',
                          borderRadius: '15px',
                          border: selectedPlan?.number === plan.number ? '2px solid #fe1848' : '2px solid #eee',
                          background: selectedPlan?.number === plan.number ? '#fff5f6' : 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'all 0.3s ease',
                          boxShadow: selectedPlan?.number === plan.number ? '0 5px 15px rgba(254, 24, 72, 0.1)' : 'none',
                          transform: selectedPlan?.number === plan.number ? 'scale(1.02)' : 'scale(1)'
                        }}
                        onMouseOver={e => { if (selectedPlan?.number !== plan.number) e.currentTarget.style.borderColor = '#fe1848' }}
                        onMouseOut={e => { if (selectedPlan?.number !== plan.number) e.currentTarget.style.borderColor = '#eee' }}
                      >
                        <div>
                          <p style={{ margin: 0, fontWeight: '700', fontSize: '1.1rem' }}>{plan.number}</p>
                          <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>{plan.actual}</p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <p style={{ margin: 0, fontWeight: '800', fontSize: '1.2rem', color: '#fe1848' }}>
                            {course.currency === "USD" ? "$" : course.currency === "INR" ? "₹" : (course.currency || "₹")}{plan.itemPrice}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(trail || rescheduleObj) && (
                <div style={{ padding: '20px', borderRadius: '15px', background: '#e8f5e9', border: '1px solid #c8e6c9', color: '#2e7d32', display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <i className="fas fa-check-circle" style={{ fontSize: '1.5rem' }}></i>
                  <span style={{ fontWeight: '600' }}>
                    {trail ? "Your 30-minute free trial session is ready!" : "Rescheduling your balance session."}
                  </span>
                </div>
              )}

            </div>
          </div>

          {/* Right Column: Order Summary & Pay */}
          <div style={{ position: width > 992 ? 'sticky' : 'static', top: '120px', height: 'fit-content' }}>
            <div style={{ background: 'white', borderRadius: '20px', padding: '30px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
              <h3 style={{ fontSize: '1.4rem', fontWeight: '800', marginBottom: '20px' }}>Order Total</h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', borderBottom: '1px solid #eee', paddingBottom: '20px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#666' }}>
                  <span>Price ({selectedPlan?.number || (trail ? "Trial" : "Reschedule") || "Selected Plan"})</span>
                  <span style={{ fontWeight: '600', color: '#333' }}>₹{basePrice || 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#666' }}>
                  <span>Platform Fee ({platformFeesPercentage}%)</span>
                  <span style={{ fontWeight: '600', color: '#333' }}>₹{platformFees || 0}</span>
                </div>
                {(paymentDetails?.couponDiscount || couponDiscountPrice) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#2e7d32', fontWeight: '600' }}>
                    <span>Discount ({couponDiscountPercentage}%)</span>
                    <span>-₹{paymentDetails?.couponDiscount || couponDiscountPrice}</span>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <span style={{ fontSize: '1.2rem', fontWeight: '800' }}>Total Amount</span>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '1.8rem', fontWeight: '800', color: '#fe1848' }}>
                    {displayTotal || (paymentDetails?.displayInfo?.currencySymbol || "₹") + (totalAmount || 0)}
                  </span>
                  {displayTotal && (
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#888', fontWeight: "600" }}>
                      Final Payment: ₹{totalAmount}
                    </p>
                  )}
                </div>
              </div>

              <button
                onClick={pay}
                disabled={loading || (!trail && !rescheduleObj && !selectedPlan.number)}
                className="pay-btn"
                style={{
                  width: '100%',
                  padding: '18px',
                  borderRadius: '15px',
                  border: 'none',
                  background: (loading || (!trail && !rescheduleObj && !selectedPlan.number)) ? '#ccc' : 'linear-gradient(135deg, #fe1848 0%, #ff4b2b 100%)',
                  color: 'white',
                  fontSize: '1.2rem',
                  fontWeight: '800',
                  cursor: (loading || (!trail && !rescheduleObj && !selectedPlan.number)) ? 'not-allowed' : 'pointer',
                  boxShadow: (loading || (!trail && !rescheduleObj && !selectedPlan.number)) ? 'none' : '0 10px 25px rgba(254, 24, 72, 0.3)',
                  transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}
                onMouseOver={e => { if (!loading && (trail || rescheduleObj || selectedPlan.number)) e.currentTarget.style.transform = 'translateY(-3px)' }}
                onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)' }}
              >
                {loading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                    <i className="fas fa-circle-notch fa-spin"></i> PROCESSING...
                  </div>
                ) : (
                  totalAmount > 0 ? "Pay Securely Now" : (trail ? "Book Free Trial" : "Confirm Booking")
                )}
              </button>

              <div style={{ marginTop: '20px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontSize: '12px', color: '#888', fontWeight: '500' }}>Trusted by 10,000+ students globally</span>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px' }}>
                  <img src={razorpayLogo} alt="Razorpay" style={{ height: '22px', opacity: 0.8 }} />
                  <div style={{ width: '1px', height: '15px', background: '#ddd' }}></div>
                  <i className="fab fa-cc-viza" style={{ fontSize: '20px', color: '#888' }}></i>
                  <i className="fab fa-cc-mastercard" style={{ fontSize: '20px', color: '#888' }}></i>
                </div>
              </div>
            </div>

            <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '10px', color: '#888', fontSize: '0.85rem', padding: '0 10px' }}>
              <i className="fas fa-lock"></i>
              <span>Payments are secure and encrypted. Session info will be shared with the teacher.</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default Payment;
